// Dashboard host (DASHBOARD_WIDGETS_PLAN.md Phase 1).
// Owns data loading, shared state (events, toast), and alert cards (D-DASH-W5).
// Individual sections are registry-driven widget components; the layout is
// resolved from the patient's saved dashboardLayout (absent = DEFAULT_DASHBOARD_LAYOUT).

import { useEffect, useState } from "react";
import type { DashboardWidgetPref, MedicalEvent, OpenIllness, OpenSymptom, Patient } from "@medassist/shared";
import { resolveDashboardLayout, DEFAULT_SYMPTOM_AUTO_CLOSE_DAYS } from "@medassist/shared";
import {
  listEvents,
  getSuggestions,
  undoEvent,
  updatePreferences,
  dismissSymptomPersistenceAlert,
  ApiError,
  type ProactiveSuggestion,
  type BatchSaveRef,
} from "../lib/api.js";
import { SuggestionCard, isSuggestionSnoozed } from "./SuggestionCard.js";
import { cacheLastSuggestion } from "../lib/suggestionCache.js";
import { SymptomFollowUpCard } from "./SymptomFollowUpCard.js";
import { QuickMedLog } from "./QuickMedLog.js";
import { QuickSymptomLog } from "./QuickSymptomLog.js";
import { QuickTreatmentLog } from "./QuickTreatmentLog.js";
import { QuickNoteLog } from "./QuickNoteLog.js";
import { getGroups } from "../lib/auth.js";
import {
  ActivityIcon, AlertTriangleIcon, PillIcon, BoltIcon, FileTextIcon, SettingsIcon,
} from "./icons.js";
import { WIDGET_REGISTRY, type WidgetProps } from "./dashboard/registry.js";
import type { WidgetDef } from "./dashboard/registry.js";
import type { DashboardLink } from "./dashboard/types.js";
import { CustomizePanel } from "./dashboard/CustomizePanel.js";

const IS_ADMIN = getGroups().includes("admin");

export function DashboardPane({
  patient,
  onNavigate,
  onPatientUpdated,
}: {
  patient: Patient;
  onNavigate: (view: DashboardLink) => void;
  onPatientUpdated: (p: Patient) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);
  const [events,  setEvents]  = useState<MedicalEvent[]>([]);
  const [suggestion,      setSuggestion]      = useState<ProactiveSuggestion | null>(null);
  const [allOpenSymptoms, setAllOpenSymptoms] = useState<OpenSymptom[]>([]);
  const [allOpenIllnesses, setAllOpenIllnesses] = useState<OpenIllness[]>([]);
  const [ptNudges,      setPtNudges]      = useState<ProactiveSuggestion[]>([]);
  const [injuryNudges,  setInjuryNudges]  = useState<ProactiveSuggestion[]>([]);
  const [persistenceAlerts, setPersistenceAlerts] = useState<ProactiveSuggestion[]>([]);
  const [toast,         setToast]         = useState<{ ref: BatchSaveRef; label: string } | null>(null);
  const [medLogOpen,    setMedLogOpen]    = useState(false);
  const [symptomLogOpen,setSymptomLogOpen]= useState(false);
  const [treatmentLogOpen,setTreatmentLogOpen] = useState(false);
  const [noteLogOpen,   setNoteLogOpen]   = useState(false);
  const [customizing,   setCustomizing]   = useState(false);
  const [editLayout,    setEditLayout]    = useState<DashboardWidgetPref[]>([]);
  const [savingLayout,  setSavingLayout]  = useState(false);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient.patientId]);

  // Keep a cross-page record of the last suggestion shown, so a thumbs-down left
  // on the Chat page's general feedback footer can still be attached to it (the
  // suggestion card itself only renders here on the Dashboard).
  useEffect(() => {
    cacheLastSuggestion(patient.patientId, suggestion);
  }, [patient.patientId, suggestion]);

  /** `quiet` skips the full-page loading state so a widget can refresh the shared
   *  events/suggestions while its own modal is open (the modal lives inside the
   *  widget tree, which the loading branch unmounts). */
  async function load(quiet = false) {
    if (!quiet) setLoading(true);
    setError(null);
    try {
      const [all, suggestions] = await Promise.all([
        listEvents(patient.patientId),
        getSuggestions(patient.patientId),
      ]);
      setEvents(all);
      const med = isSuggestionSnoozed(patient.patientId)
        ? null
        : suggestions.find((s) => s.shape === "prefilled" && s.events.length > 0) ?? null;
      setSuggestion(med);
      setAllOpenSymptoms(
        suggestions.find((s) => s.kind === "symptom_follow_up")?.openSymptoms ?? []
      );
      setAllOpenIllnesses(
        suggestions.find((s) => s.kind === "illness_follow_up")?.openIllnesses ?? []
      );
      setPtNudges(suggestions.filter((s) => s.kind === "pt_compliance"));
      setInjuryNudges(suggestions.filter((s) => s.kind === "injury_follow_up"));
      setPersistenceAlerts(suggestions.filter((s) => s.kind === "symptom_persistence_alert"));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load dashboard");
    } finally {
      if (!quiet) setLoading(false);
    }
  }

  function showToast(ref: BatchSaveRef, label: string) {
    setToast({ ref, label });
    window.setTimeout(() => setToast((t) => (t?.ref.eventId === ref.eventId ? null : t)), 8000);
  }

  async function undoToast() {
    if (!toast) return;
    const { ref } = toast;
    setToast(null);
    try {
      await undoEvent(patient.patientId, ref.occurredAt, ref.eventType, ref.eventId);
      setEvents((prev) => prev.filter((e) => e.eventId !== ref.eventId));
    } catch {
      /* undo is best-effort */
    }
  }

  async function dismissPersistenceAlert(key: string) {
    setPersistenceAlerts((prev) => prev.filter((a) => a.persistenceAlert?.key !== key));
    try {
      await dismissSymptomPersistenceAlert(patient.patientId, key);
    } catch {
      /* best-effort — it may resurface on the next scheduled re-evaluation regardless */
    }
  }

  function prependEvent(ref: BatchSaveRef, payload: Record<string, unknown>) {
    setEvents((prev) => [
      {
        patientId:   patient.patientId,
        eventId:     ref.eventId,
        eventType:   ref.eventType,
        occurredAt:  ref.occurredAt,
        payload,
        recordedAt:  ref.occurredAt,
        source:      "manual",
        sourceRefs:  [],
        actorUserId: "",
        actorRole:   "household",
        visibility:  "household",
      } as MedicalEvent,
      ...prev,
    ]);
  }

  function enterCustomize() {
    const resolved = resolveDashboardLayout(patient.dashboardLayout, WIDGET_REGISTRY);
    // Normalize so enabled items come first (preserving saved order), disabled last.
    setEditLayout([...resolved.filter((p) => p.enabled), ...resolved.filter((p) => !p.enabled)]);
    setCustomizing(true);
  }

  async function saveLayout() {
    setSavingLayout(true);
    try {
      const updated = await updatePreferences(patient.patientId, { dashboardLayout: editLayout });
      onPatientUpdated(updated);
      setCustomizing(false);
    } catch {
      setError("Failed to save dashboard layout");
    } finally {
      setSavingLayout(false);
    }
  }

  // Resolve which widgets to show and in what order.
  const resolvedLayout = resolveDashboardLayout(patient.dashboardLayout, WIDGET_REGISTRY);
  const enabledDefs    = resolvedLayout
    .filter((p) => p.enabled)
    .map((p) => WIDGET_REGISTRY.find((w) => w.id === p.id))
    .filter((w): w is WidgetDef => w != null);

  // While customizing, everything above the widget list is hidden so the list the
  // user came here to edit is at the top of the page (D-CUST-1). Symptom-persistence
  // alerts are the deliberate exception (D-CUST-2) — the only card that recommends
  // professional care.
  const showActionCards = !customizing;

  // D-SF-I3 (SYMPTOM_REMINDER_INTERVAL_PLAN.md): hide until due.
  const dueSymptoms = allOpenSymptoms.filter((s) => s.dueForCheckIn);
  const openSymptomDone = (key: string) =>
    setAllOpenSymptoms((prev) => prev.filter((x) => x.key !== key));
  const openIllnessDone = (key: string) =>
    setAllOpenIllnesses((prev) => prev.filter((x) => x.key !== key));

  const widgetProps: WidgetProps = {
    patient,
    events,
    onNavigate,
    onPatientUpdated,
    onError:        setError,
    onPrependEvent: prependEvent,
    onShowToast:    showToast,
    openSymptoms:      allOpenSymptoms,
    onOpenSymptomDone: openSymptomDone,
    openIllnesses:      allOpenIllnesses,
    onOpenIllnessDone:  openIllnessDone,
    onReload:          () => void load(true),
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl p-4 text-sm text-gray-500 dark:text-gray-400">Loading dashboard…</div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {patient.firstName}'s dashboard
        </h1>
        {customizing ? (
          <div className="flex gap-2">
            <button
              onClick={() => setCustomizing(false)}
              className="rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={() => void saveLayout()}
              disabled={savingLayout}
              className="rounded-lg bg-(--accent) px-3 py-1.5 text-sm font-medium text-(--accent-contrast) hover:bg-(--accent-hover) disabled:opacity-60"
            >
              {savingLayout ? "Saving…" : "Done"}
            </button>
          </div>
        ) : (
          <button
            onClick={enterCustomize}
            className="flex items-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <SettingsIcon size={14} />
            Customize
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Persistent symptom "see a doctor" alerts (SYMPTOM_PERSISTENCE_ALERT_PLAN.md, D-SPA-12).
          Rendered first, above every other alert/nudge card — this is the only kind that
          recommends professional medical care. */}
      {persistenceAlerts.length > 0 && (
        <div className="space-y-2">
          {persistenceAlerts.map((a) => (
            <div
              key={a.id}
              className="flex items-start gap-3 rounded-xl border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 px-4 py-3"
            >
              <AlertTriangleIcon size={16} className="mt-0.5 shrink-0 text-red-600 dark:text-red-400" />
              <div className="flex-1">
                <p className="text-sm text-gray-800 dark:text-gray-200">{a.headline}</p>
                {a.persistenceAlert && (
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                    Persisting for {a.persistenceAlert.daysPersisting} days
                  </p>
                )}
              </div>
              <button
                onClick={() => a.persistenceAlert && void dismissPersistenceAlert(a.persistenceAlert.key)}
                className="shrink-0 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                Dismiss
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Quick-log actions — 2x2 on phones, one row on sm+ (D-BTN-1). No Bedrock. */}
      {showActionCards && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <button
            onClick={() => setMedLogOpen(true)}
            className="flex flex-col items-center justify-center gap-1 rounded-xl bg-(--accent) px-3 py-3 text-sm font-semibold text-(--accent-contrast) shadow-sm hover:bg-(--accent-hover) sm:flex-row sm:gap-2"
          >
            <PillIcon size={18} />
            Medication
          </button>
          <button
            onClick={() => setSymptomLogOpen(true)}
            className="flex flex-col items-center justify-center gap-1 rounded-xl border border-(--accent) px-3 py-3 text-sm font-semibold text-(--accent) shadow-sm hover:bg-(--accent)/10 sm:flex-row sm:gap-2"
          >
            <BoltIcon size={18} />
            Symptom
          </button>
          <button
            onClick={() => setTreatmentLogOpen(true)}
            className="flex flex-col items-center justify-center gap-1 rounded-xl border border-(--accent) px-3 py-3 text-sm font-semibold text-(--accent) shadow-sm hover:bg-(--accent)/10 sm:flex-row sm:gap-2"
          >
            <ActivityIcon size={18} />
            Treatment
          </button>
          <button
            onClick={() => setNoteLogOpen(true)}
            className="flex flex-col items-center justify-center gap-1 rounded-xl border border-gray-300 dark:border-gray-700 px-3 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 sm:flex-row sm:gap-2"
          >
            <FileTextIcon size={16} />
            Note
          </button>
        </div>
      )}

      {/* Contextual alert cards — always-on, not user-toggleable (D-DASH-W5). Hidden while
          customizing (D-CUST-1) so the widget list is at the top of the page. */}
      {showActionCards && suggestion && (
        <SuggestionCard
          key={suggestion.pendingId}
          patientId={patient.patientId}
          suggestion={suggestion}
          isAdmin={IS_ADMIN}
          patientName={patient.firstName}
          timezone={patient.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
          autoCloseDays={
            patient.proactiveSuggestions?.symptomFollowUpAutoCloseDays ?? DEFAULT_SYMPTOM_AUTO_CLOSE_DAYS
          }
          complianceToleranceHours={patient.complianceToleranceHours}
          onSaved={() => { setSuggestion(null); void load(); }}
          onDismiss={() => setSuggestion(null)}
          onAddedToRegimen={() => onNavigate("regimens")}
          onSuggestionUpdated={(s) => setSuggestion(s)}
        />
      )}
      {showActionCards && dueSymptoms.length > 0 && (
        <div className="space-y-3">
          {dueSymptoms.map((s) => (
            <SymptomFollowUpCard
              key={s.key}
              patientId={patient.patientId}
              symptom={s}
              density={patient.proactiveSuggestions?.symptomCardDensity ?? "full"}
              onDone={openSymptomDone}
              siteId={s.siteId}
            />
          ))}
        </div>
      )}
      {showActionCards && ptNudges.length > 0 && (
        <div className="space-y-2">
          {ptNudges.map((n) => (
            <div
              key={n.id}
              className="flex items-center gap-3 rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/40 px-4 py-3"
            >
              <ActivityIcon size={16} className="shrink-0 text-green-600 dark:text-green-400" />
              <p className="flex-1 text-sm text-gray-800 dark:text-gray-200">{n.headline}</p>
              <button
                onClick={() => onNavigate("physical-therapy")}
                className="shrink-0 rounded-lg bg-(--accent) px-3 py-1 text-xs font-medium text-(--accent-contrast) hover:bg-(--accent-hover)"
              >
                Log session
              </button>
              <button
                onClick={() => setPtNudges((prev) => prev.filter((x) => x.id !== n.id))}
                className="shrink-0 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                Dismiss
              </button>
            </div>
          ))}
        </div>
      )}
      {showActionCards && injuryNudges.length > 0 && (
        <div className="space-y-2">
          {injuryNudges.map((n) => (
            <div
              key={n.id}
              className="flex items-center gap-3 rounded-xl border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-950/40 px-4 py-3"
            >
              <AlertTriangleIcon size={16} className="shrink-0 text-orange-500 dark:text-orange-400" />
              <p className="flex-1 text-sm text-gray-800 dark:text-gray-200">{n.headline}</p>
              <button
                onClick={() => onNavigate("injuries")}
                className="shrink-0 rounded-lg bg-orange-500 px-3 py-1 text-xs font-medium text-white hover:bg-orange-600"
              >
                Update injury
              </button>
              <button
                onClick={() => setInjuryNudges((prev) => prev.filter((x) => x.id !== n.id))}
                className="shrink-0 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                Dismiss
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Widget grid or customize panel — mutually exclusive (D-DASH-W3). */}
      {customizing ? (
        <CustomizePanel
          layout={editLayout}
          registry={WIDGET_REGISTRY}
          onChange={setEditLayout}
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {enabledDefs.map((def) => (
            <def.Widget key={def.id} {...widgetProps} />
          ))}
        </div>
      )}

      {/* As-needed medication quick-log modal */}
      {medLogOpen && (
        <QuickMedLog
          patientId={patient.patientId}
          onClose={() => setMedLogOpen(false)}
          onLogged={(ref, label) => {
            showToast(ref, label);
            void load();
          }}
          onLoggedBatch={() => void load()}
        />
      )}

      {/* Symptom quick-log modal */}
      {symptomLogOpen && (
        <QuickSymptomLog
          patientId={patient.patientId}
          onClose={() => setSymptomLogOpen(false)}
          onLogged={() => {
            setSymptomLogOpen(false);
            void load();
          }}
          onGoToChat={() => onNavigate("chat")}
        />
      )}

      {/* Treatment quick-log modal */}
      {treatmentLogOpen && (
        <QuickTreatmentLog
          patientId={patient.patientId}
          onClose={() => setTreatmentLogOpen(false)}
          onLogged={(ref, label) => {
            showToast(ref, label);
            void load(true);
          }}
          onGoToChat={() => onNavigate("chat")}
        />
      )}

      {/* Clinical note quick-add modal */}
      {noteLogOpen && (
        <QuickNoteLog
          patientId={patient.patientId}
          onClose={() => setNoteLogOpen(false)}
          onSaved={() => {
            setNoteLogOpen(false);
            void load();
          }}
        />
      )}

      {/* Undo toast for inline logs */}
      {toast && (
        <div className="fixed bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-lg bg-gray-900 dark:bg-gray-100 px-4 py-2 text-sm text-white dark:text-gray-900 shadow-lg">
          <span>{toast.label}</span>
          <button onClick={() => void undoToast()} className="font-medium underline">Undo</button>
        </div>
      )}
    </div>
  );
}
